import React from 'react'
import PropTypes from 'prop-types'

import { useUpdateCall } from '../../lib/api/update'

/**
 * Settings Component
 *
 * A React component that provides a tabbed interface for managing various settings. The component features tabs for "Theme & Page," "Site & App," "SEO," and "Consent & Languages," allowing users to switch between different settings sections.
 *
 * @module settings/Settings
 * @description A React component that provides a tabbed interface for managing various settings.
 * The component features tabs for "Theme & Page," "Site & App," "SEO," and "Consent & Languages," allowing users to switch between different settings sections.
 * @example
 * return (
 *   <Settings />
 * );
 *
 * @returns {React.Element} The rendered component with tabbed navigation for settings.
 */
export const Settings = ({ defaultData, routeData, isLocalEnvironment }) => {
  const [activeTab, setActiveTab] = React.useState(0)
  const [dataUnsaved, setDataUnsaved] = React.useState(false)
  const updateCallMutation = useUpdateCall()
  const moduleSchema = JSON.parse(routeData.apiEndPointSchema)
  const settingsData = defaultData.formData
  const dataToDisplay =
    settingsData?.length && settingsData.length > 0
      ? JSON.parse(settingsData[0].settingSchema)
      : settingsData.userSettings && JSON.parse(settingsData.userSettings)
  const [dataToSave, setDataToSave] = React.useState(dataToDisplay)

  const onDataSave = (value, indData) => {
    const data = [...dataToSave]
    data[activeTab].data[indData]['value'] = value
    setDataToSave(data)
    setDataUnsaved(true)
  }

  const saveSettings = async () => {
    const payload = { ...settingsData[0] }
    const data = JSON.stringify(dataToSave)
    payload['settingSchema'] = data
    await updateCallMutation
      .mutateAsync({
        url: isLocalEnvironment + '/updateData',
        apiEndpoint: moduleSchema.api,
        data: payload,
        id:
          settingsData.length && settingsData.length > 0
            ? settingsData[0]['settingsId']
            : settingsData['userSettingsId'],
        messageTitle: 'Settings',
        message: 'Settings saved!',
        params: '',
        isDynamic: true,
      })
      .then(() => setDataUnsaved(false))
  }

  const isLayoutDark = sessionStorage.getItem('themeLayout')
    ? sessionStorage.getItem('themeLayout')
    : false

  React.useEffect(() => {
    const unloadCallback = (event) => {
      if (dataUnsaved) {
        event.preventDefault()
        event.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', unloadCallback)
    return () => window.removeEventListener('beforeunload', unloadCallback)
  }, [dataUnsaved])

  return (
    <React.Fragment key={`${isLayoutDark}${dataUnsaved}`}>
      <div className="trtui-row">
        <div className="trtui-col-12">
          <div
            className="trtui-page-title-box trtui-d-sm-flex trtui-align-items-center trtui-justify-content-between"
            style={{ padding: '1.1rem' }}
          >
            <h4 className="trtui-mb-sm-0">{routeData.pageTitle}</h4>
          </div>
        </div>
        <div className="trtui-row">
          <div className="trtui-col trtui-project-card">
            <div className="trtui-card trtui-card-height-100">
              <div className="trtui-card-header">
                <ul
                  className="trtui-nav trtui-nav-tabs-custom trtui-rounded trtui-card-header-tabs trtui-border-bottom-0"
                  role="tablist"
                >
                  {dataToDisplay?.length > 0 &&
                    dataToDisplay.map((settings, ind) => (
                      <li key={ind} className="trtui-nav-item" role="presentation">
                        <span
                          className={`trtui-nav-link trtui-cursor-pointer ${activeTab === ind ? 'trtui-active' : ''}`}
                          data-bs-toggle="tab"
                          role="tab"
                          aria-selected="true"
                          onClick={() => setActiveTab(ind)}
                        >
                          {settings.settingName}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="trtui-card-body trtui-p-4">
                <div className="trtui-tab-content">
                  <div className={`trtui-tab-pane trtui-active trtui-show`} role="tabpanel">
                    <div className="trtui-row">
                      <div className="trtui-col-12 trtui-mb-3">
                        <h6 className="trtui-mb-sm-0">
                          {dataToDisplay?.length > 0 && dataToDisplay[activeTab].settingDescription}
                        </h6>
                      </div>
                      {dataToDisplay?.length > 0 ? (
                        dataToDisplay[activeTab].data.map((dat, datInd) => (
                          <div key={`${datInd}${activeTab}`} className="trtui-col-12">
                            <label className="trtui-form-label">{dat.placeholder}</label>
                            {dat.type === 'readOnlyText' ? (
                              <>
                                <h4>{dat.title}</h4>
                                <p>{dat.text}</p>
                              </>
                            ) : dat.type === 'text' ? (
                              <input
                                className="trtui-form-control trtui-mb-2"
                                defaultValue={dat.value}
                                onChange={(e) => onDataSave(e.target.value, datInd)}
                              />
                            ) : dat.type === 'select' ? (
                              <select
                                className="trtui-form-control trtui-mb-2"
                                onChange={(e) => onDataSave(e.target.value, datInd)}
                                defaultValue={dat.value}
                              >
                                <option value={''}>Select {dat.placeholder}</option>
                                {dat.options.map((opt, optInd) => (
                                  <option key={optInd} value={opt.text}>
                                    {opt.text}
                                  </option>
                                ))}
                              </select>
                            ) : dat.type === 'color' ? (
                              <input
                                type="color"
                                className="trtui-form-control trtui-mb-2"
                                defaultValue={dat.value}
                                onChange={(e) => onDataSave(e.target.value, datInd)}
                              />
                            ) : null}
                          </div>
                        ))
                      ) : (
                        <div className="trtui-col-12">No Data</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            {dataToDisplay?.length > 0 && (
              <button
                className="trtui-btn trtui-btn-primary"
                onClick={saveSettings}
                disabled={updateCallMutation.isLoading}
              >
                {updateCallMutation.isLoading ? (
                  <span className="trtui-d-flex trtui-align-items-center">
                    <span className="trtui-flex-grow-1 trtui-me-2">Saving...</span>
                    <span className="trtui-spinner-border trtui-flex-shrink-0" role="status">
                      <span className="trtui-visually-hidden">Saving...</span>
                    </span>
                  </span>
                ) : (
                  'Save Settings'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

Settings.propTypes = {
  defaultData: PropTypes.object,
  routeData: PropTypes.object,
  isLocalEnvironment: PropTypes.string,
}
